import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle2, MapPin, Phone, Clock, Star, Users, Dumbbell, Heart, Flame, Calendar, Shield } from 'lucide-react';

const TEMPLATES = {
  boutique: {
    name: 'Serenity Studio', tagline: 'Move with intention.', type: 'Boutique / Wellness',
    bg: '#F5F0EB', surface: '#FFFFFF', accent: '#C2847A', accentLight: '#E8D5CF', text: '#3D3027', textMuted: '#8C7B6F', border: '#E2D8D0',
    font: "'Georgia', serif",
    heroImg: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=1400&q=80&auto=format',
    hours: [{ days: 'Mon–Fri', hours: '6 AM – 8 PM' }, { days: 'Sat–Sun', hours: '8 AM – 2 PM' }],
    features: ['Yoga & Pilates Classes', 'Barre Fundamentals', 'Meditation Sessions', 'Private Training', 'Wellness Workshops', 'Monthly Retreats'],
    plans: [{ name: 'Drop-In', price: '$25', per: '' }, { name: 'Monthly', price: '$129', per: '/mo', highlight: true }, { name: 'Annual', price: '$99', per: '/mo' }],
    testimonial: { name: 'Sarah M.', text: 'This studio changed my relationship with movement. The space is beautiful, the instructors are incredible, and I finally found a practice that fits my life.', detail: 'Member, 1 year' },
    address: '422 Elm Street, Mill Valley, CA 94941',
  },
  strength: {
    name: 'Iron Republic', tagline: 'Built different.', type: 'Strength / Powerlifting',
    bg: '#F7F5F0', surface: '#FFFFFF', accent: '#0D5D3E', accentLight: '#CDE4DF', text: '#1A1A1A', textMuted: '#6B7280', border: '#E2DFD8',
    font: "'Bebas Neue', sans-serif",
    heroImg: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1400&q=80&auto=format',
    hours: [{ days: 'Members', hours: '24/7 Access' }, { days: 'Staffed', hours: 'Mon–Fri 8am–7pm' }],
    features: ['Competition Racks', 'Specialty Bars', 'Lifting Platforms', 'Personal Training', 'Open Gym', 'Powerlifting Meets'],
    plans: [{ name: 'Day Pass', price: '$20', per: '' }, { name: '12-Month', price: '$75', per: '/mo', highlight: true }, { name: 'Annual PIF', price: '$825', per: '/yr' }],
    testimonial: { name: 'Marcus T.', text: 'Best gym in the area if you\'re serious about getting stronger. Equipment is top-tier, the culture is real, and the staff actually knows what they\'re doing.', detail: 'Member, 3 years' },
    address: '112 Ironworks Way, Denver, CO 80205',
  },
  endurance: {
    name: 'Volt Athletics', tagline: 'Outwork everything.', type: 'CrossFit / HIIT / Endurance',
    bg: '#0A0A1A', surface: '#111127', accent: '#6366F1', accentLight: '#312E81', text: '#F8FAFC', textMuted: '#94A3B8', border: '#1E293B',
    font: "'Manrope', sans-serif",
    heroImg: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1400&q=80&auto=format',
    hours: [{ days: 'Classes', hours: '5:30 AM – 7:30 PM' }, { days: 'Open Gym', hours: '5 AM – 9 PM' }],
    features: ['WOD Programming', 'HIIT Classes', 'Running Club', 'Olympic Lifting', 'Endurance Training', 'Community Competitions'],
    plans: [{ name: 'Drop-In', price: '$30', per: '' }, { name: 'Unlimited', price: '$189', per: '/mo', highlight: true }, { name: '6-Month', price: '$159', per: '/mo' }],
    testimonial: { name: 'Kai L.', text: 'The programming is sharp, the coaches push you without breaking you, and the community is the most fired-up group of people I\'ve ever trained with.', detail: 'Member, 2 years' },
    address: '800 Market St, Oakland, CA 94607',
  },
};

export default function TemplatePreview() {
  const { templateId } = useParams();
  const t = TEMPLATES[templateId];
  useEffect(() => { window.scrollTo(0, 0); }, [templateId]);

  if (!t) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
      <div className="text-center">
        <p className="text-white text-lg mb-4">Template not found</p>
        <Link to="/huscleos" className="text-[#FF2E00] font-bold">Back to HuscleOS</Link>
      </div>
    </div>
  );

  const isDark = templateId === 'endurance';

  return (
    <div style={{ background: t.bg, fontFamily: t.font }}>
      {/* Back to HuscleOS */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-12">
          <Link to="/#templates" className="flex items-center gap-2 text-sm font-bold text-white hover:text-gray-300 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="template-back">
            <ArrowLeft size={16} /> Back to HuscleOS
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-gray-500" style={{ fontFamily: 'Manrope' }}>Previewing: {t.type}</span>
            <Link to="/#quiz" className="text-xs font-bold tracking-widest uppercase px-4 py-1.5 bg-[#FF2E00] text-white hover:opacity-90 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="template-build-demo">
              BUILD MY DEMO
            </Link>
          </div>
        </div>
      </div>

      {/* Fake Nav */}
      <nav className="pt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <span className={`text-2xl font-bold uppercase tracking-tight ${isDark ? 'text-white' : ''}`} style={{ fontFamily: t.font, color: isDark ? '#fff' : t.text }}>
            {t.name}
          </span>
          <div className="hidden md:flex items-center gap-6">
            {['About', 'Programs', 'Pricing', 'Contact'].map(l => (
              <span key={l} className="text-sm font-medium cursor-default" style={{ color: isDark ? '#94A3B8' : t.textMuted }}>{l}</span>
            ))}
            <span className="text-sm font-bold px-5 py-2 cursor-default" style={{ background: t.accent, color: '#fff' }}>Join Now</span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center" style={{ background: t.bg }}>
        <div className="absolute inset-0">
          <img src={t.heroImg} alt="" className="w-full h-full object-cover" style={{ opacity: isDark ? 0.3 : 0.15 }} loading="eager" />
          {isDark && <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,26,0.4), rgba(10,10,26,0.95))' }} />}
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-20">
          <p className="text-sm font-bold tracking-[0.15em] uppercase mb-4" style={{ color: t.accent }}>{t.type}</p>
          <h1 className="text-5xl sm:text-7xl leading-[0.9] tracking-tight uppercase mb-4" style={{ fontFamily: t.font, color: isDark ? '#fff' : t.text }}>
            {t.name}.<br /><span style={{ color: t.accent }}>{t.tagline}</span>
          </h1>
          <p className="text-base max-w-lg mb-6" style={{ color: isDark ? '#94A3B8' : t.textMuted }}>
            This is a preview of the <strong>{t.type}</strong> template. In your live HuscleOS environment, this would be your gym's branded website with real content, real lead capture, and real conversion flows.
          </p>
          <div className="flex gap-3">
            <span className="text-sm font-bold px-6 py-3 cursor-default" style={{ background: t.accent, color: '#fff' }}>Join Now <ArrowRight size={14} className="inline ml-1" /></span>
            <span className="text-sm font-bold px-6 py-3 border-2 cursor-default" style={{ color: isDark ? '#fff' : t.text, borderColor: isDark ? '#fff' : t.text }}>Book a Tour</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16" style={{ background: isDark ? t.surface : '#fff' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-sm font-bold tracking-[0.15em] uppercase mb-3" style={{ color: t.accent }}>What We Offer</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight uppercase mb-10" style={{ fontFamily: t.font, color: isDark ? '#fff' : t.text }}>Programs & Services</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {t.features.map((f, i) => (
              <div key={i} className="p-5 border" style={{ borderColor: t.border, background: isDark ? t.bg : t.bg }}>
                <CheckCircle2 size={18} style={{ color: t.accent }} className="mb-3" />
                <p className="text-sm font-bold" style={{ color: isDark ? '#fff' : t.text }}>{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16" style={{ background: t.bg }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-sm font-bold tracking-[0.15em] uppercase mb-3" style={{ color: t.accent }}>Membership</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight uppercase" style={{ fontFamily: t.font, color: isDark ? '#fff' : t.text }}>Find Your Plan</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {t.plans.map((p, i) => (
              <div key={i} className="p-6 border text-center" style={{ borderColor: p.highlight ? t.accent : t.border, background: isDark ? t.surface : '#fff', boxShadow: p.highlight ? `0 0 0 2px ${t.accent}` : 'none' }}>
                {p.highlight && <span className="text-[10px] font-bold tracking-widest uppercase px-3 py-1 inline-block mb-3" style={{ background: t.accent, color: '#fff' }}>POPULAR</span>}
                <h3 className="text-lg font-bold mb-1" style={{ color: isDark ? '#fff' : t.text }}>{p.name}</h3>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-3xl font-bold" style={{ color: isDark ? '#fff' : t.text }}>{p.price}</span>
                  {p.per && <span className="text-sm" style={{ color: isDark ? '#94A3B8' : t.textMuted }}>{p.per}</span>}
                </div>
                <span className="text-sm font-bold px-5 py-2 inline-block cursor-default" style={{ background: p.highlight ? t.accent : 'transparent', color: p.highlight ? '#fff' : t.accent, border: p.highlight ? 'none' : `2px solid ${t.accent}` }}>Select Plan</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16" style={{ background: isDark ? t.surface : '#fff' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill={t.accent} style={{ color: t.accent }} />)}</div>
          <p className="text-lg leading-relaxed mb-6 italic" style={{ color: isDark ? '#D4D4D8' : t.text }}>"{t.testimonial.text}"</p>
          <p className="font-bold" style={{ color: isDark ? '#fff' : t.text }}>{t.testimonial.name}</p>
          <p className="text-sm" style={{ color: isDark ? '#94A3B8' : t.textMuted }}>{t.testimonial.detail}</p>
        </div>
      </section>

      {/* Contact / Map */}
      <section className="py-16" style={{ background: t.bg }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm font-bold tracking-[0.15em] uppercase mb-3" style={{ color: t.accent }}>Visit Us</p>
              <h2 className="text-2xl tracking-tight uppercase mb-6" style={{ fontFamily: t.font, color: isDark ? '#fff' : t.text }}>Find {t.name}</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin size={16} style={{ color: t.accent, marginTop: 2 }} />
                  <p className="text-sm font-medium" style={{ color: isDark ? '#D4D4D8' : t.text }}>{t.address}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={16} style={{ color: t.accent, marginTop: 2 }} />
                  <p className="text-sm font-medium" style={{ color: isDark ? '#D4D4D8' : t.text }}>(555) 123-4567</p>
                </div>
                {t.hours.map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Clock size={16} style={{ color: t.accent, marginTop: 2 }} />
                    <p className="text-sm" style={{ color: isDark ? '#D4D4D8' : t.text }}><span className="font-medium">{h.days}:</span> {h.hours}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden h-64 border" style={{ borderColor: t.border, background: isDark ? '#1E293B' : '#e5e7eb' }}>
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-sm" style={{ color: isDark ? '#475569' : '#9CA3AF' }}>Map placeholder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA back to HuscleOS */}
      <section className="py-16 text-center" style={{ background: isDark ? '#0F0F2A' : '#fff' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-sm font-bold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: '#FF2E00' }}>HUSCLEOS TEMPLATE PREVIEW</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight uppercase mb-4" style={{ fontFamily: 'Bebas Neue', color: isDark ? '#fff' : '#1A1A1A' }}>
            THIS COULD BE YOUR GYM.
          </h2>
          <p className="text-sm mb-6" style={{ fontFamily: 'Manrope', color: isDark ? '#94A3B8' : '#6B7280' }}>
            Every template becomes a fully custom website + CRM + growth engine for your business.
          </p>
          <Link to="/huscleos#quiz" className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-8 py-4 hover:opacity-90 transition-colors" style={{ fontFamily: 'Manrope', background: '#FF2E00', color: '#fff' }}>
            BUILD MY DEMO <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Simple footer */}
      <footer className="py-8 border-t" style={{ background: isDark ? '#0A0A1A' : t.bg, borderColor: t.border }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs" style={{ color: isDark ? '#475569' : t.textMuted }}>
            {t.name} — A HuscleOS Template Preview. This is a demo website.
          </p>
        </div>
      </footer>
    </div>
  );
}
